import dayjs from "dayjs";
import { isNil } from "lodash";
import { useMemo, useState } from "react";
import { Col, Divider, Row, Space, Statistic } from "antd";
import { Column, Pie, Scatter } from "@ant-design/charts";
import { useTranslation } from "react-i18next";

import { Card } from "misc/card";
import { Result } from "misc/results";
import { StatisticsBox } from "misc/boxes";
import { useParams } from "misc/hooks";
import { useSubfleets } from "misc/api/fleetconfiguration";
import { useFleetBatteryOverview } from "misc/api/datalayer";
import { clientUrls } from "misc/urls";
import { Empty } from "misc/empty";
import { DatetimeField, NumberField } from "misc/fields";
import { BatteryTypeSelector } from "misc/selectors";
import { daysToYears, getQuantileFromSortedValues } from "misc/helpfunctions";
import { stsColors } from "styles";

import ModuleSettings from "modules/ModuleSettings";
import SideHeader from "navigation/SideHeader";

const TABLEAU_COLORS = [
    "#1F77B4",
    "#FF7F0E",
    "#2CA02C",
    "#D62728",
    "#9467BD",
    "#8C564B",
    "#E377C2",
    "#7F7F7F",
    "#BCBD22",
    "#17BECF",
    "#AEC7E8",
    "#FFBB78",
    "#98DF8A",
    "#FF9896",
    "#C5B0D5",
    "#C49C94",
    "#F7B6D2",
    "#C7C7C7",
    "#DBDB8D",
    "#9EDAE5"
];

const BmOverviewComponent = () => {
    const [t] = useTranslation();
    const [sohBatteryType, setSohBatteryType] = useState("all");
    const [pmScoreBatteryType, setPmScoreBatteryType] = useState("all");
    const { subfleets } = useSubfleets({ suspense: true });

    const { params, setParams } = useParams({
        options: [{ name: "fleet", persist: "global", allowed: value => subfleets.find(fleet => fleet.subfleetId === value) }]
    });

    const fleet = useMemo(() => subfleets.find(fleet => fleet.subfleetId === params.fleet), [params.fleet, subfleets]);
    const filter = useMemo(() => ({ hierarchy: fleet?.organisationalUnits, vehicles: fleet?.vehicles }), [fleet]);

    const { fleetBatteryOverview, isLoading, isRefetching } = useFleetBatteryOverview({
        filter,
        enabled: Boolean(fleet),
        keepPreviousData: true
    });

    const rows = fleetBatteryOverview?.data ?? [];
    const statistics = fleetBatteryOverview?.statistics ?? {};
    const sortedSohValues = useMemo(
        () =>
            rows
                .filter(row => sohBatteryType === "all" || (row.batteryType || "unknown") === sohBatteryType)
                .map(row => row.soh)
                .filter(value => value != null && value !== "")
                .map(value => Number(value))
                .filter(value => Number.isFinite(value))
                .sort((left, right) => left - right),
        [rows, sohBatteryType]
    );

    const batteryTypeDistribution = useMemo(() => {
        const distribution = rows.reduce((acc, row) => {
            if (row.isDummy !== false) return acc;
            const batteryType = row.batteryTypeDisplayName || row.batteryType || t("t_unknown");
            acc[batteryType] = (acc[batteryType] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(distribution).map(([batteryType, count]) => ({ batteryType, count }));
    }, [rows, t]);

    const batteryTypes = useMemo(() => batteryTypeDistribution.map(item => item.batteryType), [batteryTypeDistribution]);

    const scatterBatteryTypes = useMemo(
        () => [...new Set([...batteryTypes, ...rows.map(row => row.batteryTypeDisplayName || row.batteryType || t("t_unknown"))])],
        [batteryTypes, rows, t]
    );

    const sohDistribution = useMemo(() => {
        if (!sortedSohValues.length) return [];

        const binSize = 5;
        const startRange = Math.floor(Math.min(...sortedSohValues) / binSize) * binSize;
        const endRange = Math.ceil(Math.max(...sortedSohValues) / binSize) * binSize;
        const data = [];

        for (let start = startRange; start <= endRange; start += binSize) {
            const end = start + binSize;
            const count = sortedSohValues.filter(soh => soh >= start && soh < end).length;
            if (count > 0) {
                data.push({ range: `${start}-${end}%`, count });
            }
        }

        return data;
    }, [sortedSohValues]);

    const sohStatistics = useMemo(() => {
        return {
            p10: getQuantileFromSortedValues(sortedSohValues, 0.1),
            median: getQuantileFromSortedValues(sortedSohValues, 0.5),
            p90: getQuantileFromSortedValues(sortedSohValues, 0.9)
        };
    }, [sortedSohValues]);

    const pmScoreRows = useMemo(
        () => rows.filter(row => !isNil(row.pmScore) && (pmScoreBatteryType === "all" || (row.batteryType || "unknown") === pmScoreBatteryType)),
        [rows, pmScoreBatteryType]
    );

    const pmScoreDistribution = useMemo(
        () =>
            [...new Set(pmScoreRows.map(row => row.pmScore))]
                .sort((left, right) => left - right)
                .map(score => ({
                    pmScore: `${score}`,
                    count: pmScoreRows.filter(row => row.pmScore === score).length
                })),
        [pmScoreRows]
    );

    const sohOverAge = useMemo(
        () =>
            rows
                .filter(row => row.soh != null && row.lifecycleStart)
                .map(row => ({
                    batteryId: row.batteryId,
                    vin: row.vin,
                    batteryType: row.batteryTypeDisplayName || row.batteryType || t("t_unknown"),
                    batteryAgeYears: daysToYears(dayjs().diff(dayjs(row.lifecycleStart), "day")),
                    batteryAgeMonths: dayjs().diff(dayjs(row.lifecycleStart), "month"),
                    soh: row.soh
                })),
        [rows, t]
    );

    const sohOverMileage = useMemo(
        () =>
            rows
                .filter(row => row.soh != null && row.batteryMileageKm != null)
                .map(row => ({
                    batteryId: row.batteryId,
                    vin: row.vin,
                    batteryType: row.batteryTypeDisplayName || row.batteryType || t("t_unknown"),
                    batteryMileageKm: row.batteryMileageKm,
                    soh: row.soh
                })),
        [rows, t]
    );

    return (
        <SideHeader>
            <Row gutter={[10, 10]}>
                <Col span={24}>
                    <ModuleSettings
                        values={params}
                        fleetOptions={{ redirectUrl: clientUrls.modules.vlv.fleetConfiguration() }}
                        hideViewOptions
                        hideExportOptions
                        onChange={values => {
                            setParams({ ...params, ...values });
                            setSohBatteryType("all");
                            setPmScoreBatteryType("all");
                        }}
                    />
                </Col>

                {fleet ? (
                    <>
                        <Col span={24}>
                            <Card title="t_bm_fleet_overview" isLoading={isLoading || isRefetching}>
                                <Row gutter={[16, 0]} align="middle">
                                    <Col xs={24} lg={5}>
                                        <Row justify="center">
                                            <Space direction="vertical" size={20} align="center" style={{ textAlign: "center" }}>
                                                <StatisticsBox
                                                    title="t_bm_total_electric_vehicles"
                                                    data={statistics.totalElectricVehicles ?? 0}
                                                    precision={0}
                                                />
                                                <StatisticsBox
                                                    title="t_bm_known_battery_assignments"
                                                    data={statistics.totalBatteries ?? 0}
                                                    precision={0}
                                                />
                                                <StatisticsBox
                                                    title="t_bm_last_soh_evaluated_at"
                                                    data={statistics.lastSohEvaluatedAt ?? ""}
                                                    formatter={value => <DatetimeField value={value} format="DD.MM.YY HH:mm" />}
                                                />
                                                <StatisticsBox
                                                    title="t_bm_last_pm_score_evaluated_at"
                                                    data={statistics.lastPmScoreEndAt ?? ""}
                                                    formatter={value => <DatetimeField value={value} format="DD.MM.YY HH:mm" />}
                                                />
                                            </Space>
                                        </Row>
                                    </Col>
                                    <Col xs={0} lg={1}>
                                        <Row justify="center">
                                            <Divider type="vertical" style={{ height: 350 }} />
                                        </Row>
                                    </Col>
                                    <Col xs={24} lg={18}>
                                        <Pie
                                            data={batteryTypeDistribution}
                                            color={TABLEAU_COLORS}
                                            angleField="count"
                                            colorField="batteryType"
                                            meta={{ batteryType: { values: batteryTypes } }}
                                            height={370}
                                            radius={0.9}
                                            innerRadius={0.5}
                                            label={{ type: "outer", content: "{name}" }}
                                            tooltip={{
                                                formatter: datum => ({ name: datum.batteryType, value: datum.count })
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        <Col xs={24} xl={12}>
                            <Card
                                title="t_soh_distribution"
                                height={550}
                                loading={isLoading || isRefetching}
                                style={{ display: "flex", flexDirection: "column" }}
                                bodyStyle={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 8, paddingTop: 12 }}
                            >
                                <Space direction="vertical" size={8} style={{ width: "100%", flexShrink: 0 }}>
                                    <BatteryTypeSelector batteries={rows} value={sohBatteryType} onChange={setSohBatteryType} />
                                    <NumberField prefix={`${t("t_number_of_batteries")}:`} value={sortedSohValues.length} inline />
                                </Space>
                                {sohDistribution.length ? (
                                    <>
                                        <Row align="middle" gutter={[0, 0]} wrap={false} style={{ flexShrink: 0 }}>
                                            <Col flex="1 1 0">
                                                <Row justify="center">
                                                    <Statistic
                                                        title="P10"
                                                        value={sohStatistics.p10}
                                                        suffix="%"
                                                        precision={1}
                                                        style={{ textAlign: "center" }}
                                                    />
                                                </Row>
                                            </Col>
                                            <Col flex="0 0 auto">
                                                <Divider type="vertical" style={{ height: 48 }} />
                                            </Col>
                                            <Col flex="1 1 0">
                                                <Row justify="center">
                                                    <Statistic
                                                        title="Median"
                                                        value={sohStatistics.median}
                                                        suffix="%"
                                                        precision={1}
                                                        style={{ textAlign: "center" }}
                                                    />
                                                </Row>
                                            </Col>
                                            <Col flex="0 0 auto">
                                                <Divider type="vertical" style={{ height: 48 }} />
                                            </Col>
                                            <Col flex="1 1 0">
                                                <Row justify="center">
                                                    <Statistic
                                                        title="P90"
                                                        value={sohStatistics.p90}
                                                        suffix="%"
                                                        precision={1}
                                                        style={{ textAlign: "center" }}
                                                    />
                                                </Row>
                                            </Col>
                                        </Row>

                                        <Divider style={{ margin: 0 }} />

                                        <Column
                                            data={sohDistribution}
                                            xField="range"
                                            yField="count"
                                            autoFit
                                            style={{ flex: 1, minHeight: 0 }}
                                            color={stsColors.blue}
                                            label={false}
                                            xAxis={{ title: { text: t("t_soh_category") } }}
                                            yAxis={{ title: { text: t("t_number_of_batteries") } }}
                                        />
                                    </>
                                ) : (
                                    <Empty subTitle="t_bm_no_soh_for_selection" style={{ padding: 0 }} />
                                )}
                            </Card>
                        </Col>

                        <Col xs={24} xl={12}>
                            <Card
                                title="t_pm_score_distribution"
                                height={550}
                                loading={isLoading || isRefetching}
                                style={{ display: "flex", flexDirection: "column" }}
                                bodyStyle={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 8, paddingTop: 12 }}
                            >
                                <Space direction="vertical" size={8} style={{ width: "100%", flexShrink: 0 }}>
                                    <BatteryTypeSelector batteries={rows} value={pmScoreBatteryType} onChange={setPmScoreBatteryType} />
                                    <NumberField prefix={`${t("t_number_of_batteries")}:`} value={pmScoreRows.length} inline />
                                </Space>
                                {pmScoreDistribution.length ? (
                                    <Column
                                        data={pmScoreDistribution}
                                        xField="pmScore"
                                        yField="count"
                                        meta={{
                                            pmScore: { alias: t("t_bm_pm_score") },
                                            count: { alias: t("t_number_of_batteries") }
                                        }}
                                        autoFit
                                        style={{ flex: 1, minHeight: 0 }}
                                        color={stsColors.blue}
                                        label={false}
                                        xAxis={{ title: { text: t("t_bm_pm_score") } }}
                                        yAxis={{ title: { text: t("t_number_of_batteries") } }}
                                    />
                                ) : (
                                    <Empty subTitle="t_bm_no_pm_score_for_selection" style={{ padding: 0 }} />
                                )}
                            </Card>
                        </Col>

                        <Col span={24}>
                            <Card title="t_soh_over_age" height={460} isLoading={isLoading || isRefetching}>
                                {sohOverAge.length ? (
                                    <Scatter
                                        data={sohOverAge}
                                        legend={{ position: "top", radio: {} }}
                                        color={TABLEAU_COLORS}
                                        xField="batteryAgeYears"
                                        yField="soh"
                                        height={360}
                                        colorField="batteryType"
                                        shape="circle"
                                        pointStyle={{ fillOpacity: 0.8, lineWidth: 0.8 }}
                                        size={4}
                                        xAxis={{ title: { text: t("t_age_years") } }}
                                        yAxis={{ title: { text: `${t("t_bm_soh")} %` } }}
                                        meta={{
                                            batteryType: { values: scatterBatteryTypes, alias: t("t_bm_battery_type") },
                                            batteryId: { alias: t("t_bm_battery_id") },
                                            vin: { alias: t("t_vin") },
                                            batteryAgeYears: {
                                                alias: t("t_age_years"),
                                                formatter: value => `${value} ${t("t_bm_years")}`
                                            },
                                            batteryAgeMonths: {
                                                alias: t("t_age_years"),
                                                formatter: value => {
                                                    if (isNil(value)) return "—";
                                                    const years = Math.floor(value / 12);
                                                    const months = value % 12;
                                                    const yearText = `${years} ${t(years === 1 ? "t_bm_year" : "t_bm_years")}`;
                                                    const monthText = `${months} ${t(months === 1 ? "t_bm_month" : "t_bm_months")}`;

                                                    if (years === 0) return monthText;
                                                    if (months === 0) return yearText;
                                                    return `${yearText} ${monthText}`;
                                                }
                                            },
                                            soh: {
                                                alias: t("t_bm_soh"),
                                                formatter: value => {
                                                    if (isNil(value)) return "—";
                                                    return `${value.toFixed(1)} %`;
                                                }
                                            }
                                        }}
                                        tooltip={{ fields: ["batteryId", "batteryType", "vin", "batteryAgeMonths", "soh"] }}
                                    />
                                ) : (
                                    <Empty height={360} />
                                )}
                            </Card>
                        </Col>

                        <Col span={24}>
                            <Card title="t_soh_over_mileage" height={460} isLoading={isLoading || isRefetching}>
                                {sohOverMileage.length ? (
                                    <Scatter
                                        data={sohOverMileage}
                                        legend={{ position: "top", radio: {} }}
                                        color={TABLEAU_COLORS}
                                        xField="batteryMileageKm"
                                        yField="soh"
                                        height={360}
                                        colorField="batteryType"
                                        shape="circle"
                                        pointStyle={{ fillOpacity: 0.8, lineWidth: 0.8 }}
                                        size={4}
                                        xAxis={{ title: { text: t("t_hv_battery_mileage_km") } }}
                                        yAxis={{ title: { text: `${t("t_bm_soh")} %` } }}
                                        meta={{
                                            batteryType: { values: scatterBatteryTypes, alias: t("t_bm_battery_type") },
                                            batteryId: { alias: t("t_bm_battery_id") },
                                            vin: { alias: t("t_vin") },
                                            batteryMileageKm: {
                                                alias: t("t_hv_battery_mileage_km"),
                                                formatter: value => {
                                                    if (isNil(value)) return "—";
                                                    return `${value.toFixed(1)} km`;
                                                }
                                            },
                                            soh: {
                                                alias: t("t_bm_soh"),
                                                formatter: value => {
                                                    if (isNil(value)) return "—";
                                                    return `${value.toFixed(1)} %`;
                                                }
                                            }
                                        }}
                                        tooltip={{ fields: ["batteryId", "batteryType", "vin", "batteryMileageKm", "soh"] }}
                                    />
                                ) : (
                                    <Empty height={360} />
                                )}
                            </Card>
                        </Col>
                    </>
                ) : (
                    <Col span={24}>
                        <Card height={500}>
                            <Result type="noFleetSelected" url={clientUrls.modules.bm.overview()} />
                        </Card>
                    </Col>
                )}
            </Row>
        </SideHeader>
    );
};

export default BmOverviewComponent;
